// This is a catch-all route.
// It is the entry point for handling entity routes from Drupal.
import {GetStaticPathsResult, GetStaticPropsResult} from 'next';
import {DrupalNode, DrupalTaxonomyTerm} from 'next-drupal';

import {getMenus, menuAwareRevalidate} from '@/lib/get-menus';
import {drupal} from '@/lib/drupal';
import {GetStaticPathsContext} from 'next/types';
import {
  CustomMenuLink,
  DrupalMainMenuLinkContent,
  DrupalVideoMedia,
  DrupalWebform,
  TranslatedUrls,
} from "@/types/hygiena-types";
import {getBlurMap} from "@/lib/get-blur-map";
import {testApiCompatibility} from "@/lib/test-api-compatibility";
import {useEffect} from "react";
import {useRouter} from "next/router";

import {purgeSlug} from "@/lib/purge-slug";
import {getAlertBanner} from "@/lib/get-alert-banner";
import {getDictionary} from "@/lib/dictionaries";
import {getLeadgenWebform} from "@/lib/get-leadgen-webform";
import {ALLOWED_LOCALES} from "@/lib/constants";
import {addDynamicOGImage, replaceLeadingCmsUrlDeep, sanitizeCanonicalMetatags} from "@/lib/metatag";
import {safeDecode} from "@/lib/utils";

import {ENTITY_TYPES, REVALIDATE_TTL} from "@/lib/entity-page/constants";
import {EntityPageProps} from "@/lib/entity-page/types";
import {EntityRenderer} from "@/lib/entity-page/entity-renderer";
import {getEntityParams} from "@/lib/entity-page/get-entity-params";
import {extractContactFormFields, processEntity} from "@/lib/entity-page/process-entity";
import {getSourceLanguageDestination, hasPublishedTranslation} from "@/lib/source-language";

export default function EntityPage({
  entity,
  additionalContent,
  redirectTo,
}: EntityPageProps) {
  const router = useRouter();

  useEffect(() => {
    if (redirectTo) router.push(redirectTo);
  });

  return <EntityRenderer entity={entity} additionalContent={additionalContent}/>;
}

function normalizeAliasToPathObject(alias: string, locale?: string) {
  let cleanAlias = alias.startsWith('/') ? alias : `/${alias}`;

  // Strip locale prefix if Drupal returns aliases like /en/foo
  // and Next handles locale separately.
  if (locale && cleanAlias === `/${locale}`) {
    cleanAlias = '/';
  } else if (locale && cleanAlias.startsWith(`/${locale}/`)) {
    cleanAlias = cleanAlias.slice(locale.length + 1);
  }

  const slug = cleanAlias.replace(/^\/+|\/+$/g, '').split('/').filter(Boolean);

  return {
    params: {slug},
    ...(locale ? {locale} : {}),
  };
}

async function fetchStaticAliases(locales: string[], entity_types: string[], bundles: {[key: string]: string[]} = {}): Promise<string[]> {
  const drupalBaseUrl = process.env.NEXT_PUBLIC_DRUPAL_BASE_URL;

  if (!drupalBaseUrl) {
    throw new Error('NEXT_PUBLIC_DRUPAL_BASE_URL is not set.');
  }

  const url = new URL('/api/v1/build-static-paths', drupalBaseUrl);
  url.searchParams.set('entity_types', entity_types.join(','));
  url.searchParams.set('langcodes', locales.join(','));
  for (const bundle in bundles) {
    url.searchParams.set(`bundles[${bundle}]`, bundles[bundle].join(','));
  }
  url.searchParams.set('flat', '1');

  console.log(`Fetching static paths: ${url.toString()}`);

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(
      `Failed to fetch static paths for locales "${locales.join(', ')}": ${response.status} ${response.statusText}`
    );
  }

  return (await response.json()) as string[];
}

export async function getStaticPaths(
  context: GetStaticPathsContext,
): Promise<GetStaticPathsResult> {
  const limit = process.env.ITEMS_LIMIT ? parseInt(process.env.ITEMS_LIMIT, 10) : 200;

  const locales = process.env.LOCAL_PROD_BUILD
    ? (context.locales ?? [])
    : [context.defaultLocale ?? 'en'];

  const seen = new Set<string>();
  const paths: Array<{params: {slug: string[]}; locale?: string}> = [];

  for (const locale of locales) {
    const aliases = await fetchStaticAliases(
      ALLOWED_LOCALES,
      ['node', 'taxonomy_term', 'media'],
      {
        node: ['page', 'article', 'event', 'key_value_page', 'product', 'landing_page', 'html_pack'],
        taxonomy_term: ['product_type', 'product_class', 'industry_segments'],
        media: ['video'],
      },
    );

    for (const alias of aliases) {
      const parts = alias.split('/').filter(Boolean);
      const langcode = ALLOWED_LOCALES.includes(parts[0]) ? parts[0] : 'en';
      const path = '/' + parts.slice(1).join('/');
      const key = `${locale}:${alias.toLowerCase()}`;
      if (seen.has(key)) {
        continue;
      }

      seen.add(key);
      paths.push(normalizeAliasToPathObject(path, langcode));
    }
  }

  return {
    paths: paths.slice(0, limit),
    fallback: 'blocking',
  };
}

export async function getStaticProps(context): Promise<GetStaticPropsResult<EntityPageProps>> {
  // Find a matching path from Drupal from context.
  const start = performance.now();
  const NEXT_BUILD_MODE = process.env?.["NEXT_BUILD_MODE"] ?? false;
  const requestedLocale = context.locale;
  const defaultLocale = context.defaultLocale ?? 'en';
  const path = await drupal.translatePathFromContext(context);
  const customMenu: DrupalMainMenuLinkContent[] = [];
  let translatedUrls: TranslatedUrls | null;
  const redirectTo = '';
  const slug = context?.params?.slug?.[0] ? context.params.slug.join('/') : 'unknown';

  if (process.env.NODE_ENV == 'development') {
    try {
      await testApiCompatibility(ENTITY_TYPES, drupal);
    } catch (error) {
      console.log(error);
    }
  }

  if (!path) {
    console.log(`Failed to fetch resource: path not found for slug: ${slug}.`);
    if (!NEXT_BUILD_MODE) {
      const destination = context.locale !== context.defaultLocale ? `/${context.locale}` : '/';
      return {
        redirect: {
          destination,
          statusCode: 301,
        },
      };
    } else {
      return {
        notFound: true,
        revalidate: 60,
      };
    }
  }

  // Handle redirects.
  if (path?.redirect) {
    const [redirect] = path.redirect;
    let redirectStatus = 302 as 301 | 302 | 303 | 307 | 308;
    // See if the redirect has resolved to a path (not just /node/n or /taxonomy/term/n etc).
    const redirectTo = (path?.resolved?.length && path?.entity?.path?.length) ? path.entity.path : redirect.to;
    try {
      redirectStatus = parseInt(redirect.status) as 301 | 302 | 303 | 307 | 308;
    } catch (error) {
      console.log(`Failed to parse redirect status code.`);
    }
    if (!NEXT_BUILD_MODE) {
      console.log(`Redirecting (${redirectStatus}) : ${redirectTo}`);
      return {
        redirect: {
          destination: redirectTo,
          statusCode: redirectStatus,
        },
      };
    } else {
      console.log(`Attempting redirect in build mode, returning 404 for: ${redirectTo}`);
      return {
        notFound: true,
        revalidate: 60,
      };
    }
  }
  // If Drupal resolved it to a different path, redirect to the canonical one.
  else if (path?.entity?.path && context.params?.slug && !NEXT_BUILD_MODE) {
    const localePrefix = context.locale !== context.defaultLocale ? `/${context.locale}` : '';
    const requestedPath = `${localePrefix}/${(context.params.slug as string[]).join('/')}`;
    const canonicalPath = path.entity.path;

    // compare the decoded form to avoid looping on encoding-only differences i.e. accented characters.
    if (safeDecode(requestedPath) !== safeDecode(canonicalPath)) {
      return {
        redirect: {
          destination: canonicalPath,
          statusCode: 301,
        },
      };
    }
  }

  const type = path?.jsonapi?.resourceName ?? '';

  if (!ENTITY_TYPES.includes(type)) {
    // @todo set this back to not found later.
    console.log(`Failed to fetch resource: ${path?.jsonapi?.individual ?? 'unknown'}`);
    if (!NEXT_BUILD_MODE) {
      const destination = context.locale !== context.defaultLocale ? `/${context.locale}` : '/';
      return {
        redirect: {
          destination,
          statusCode: 301,
        },
      };
    } else {
      return {
        notFound: true,
        revalidate: 60,
      };
    }
  }

  const params = getEntityParams(type, {
    resourceVersion: context?.preview ? context?.previewData?.resourceVersion : undefined,
  });

  let entity: DrupalNode | DrupalTaxonomyTerm | DrupalVideoMedia | null = null;
  // We always get the entity via an initial call, any subsequent requests for required
  // data should be amalgamated into a sub-request to prevent hammering the server.
  try {
    const s = performance.now();
    entity = await drupal.getResourceFromContext<
      DrupalNode | DrupalTaxonomyTerm
    >(type, context, {
      params: params.getQueryObject(),
    });
    if (!NEXT_BUILD_MODE) {
      console.log(`${((performance.now() - s) / 1000).toFixed(2)} seconds to get entity ${slug}.`)
    }
  } catch (error) {
    console.log(`Failed to fetch resource: ${path?.jsonapi?.individual ?? 'unknown'}`);
  }
  if (!entity && requestedLocale !== defaultLocale && path?.entity?.uuid) {
    // Fetch the source-language entity so we can redirect unpublished or
    // missing translations to that URL instead of rendering a fallback page.
    entity = await drupal.getResource<
      DrupalNode | DrupalTaxonomyTerm | DrupalVideoMedia
    >(type, path?.entity?.uuid, {
      params: params.getQueryObject(),
    });
  }

  // At this point, we know the path exists and it points to a resource. If we
  // receive an error, it means something went wrong on Drupal. Let's log an
  // error, so we can see what's going on locally and return not found, so
  // we don't break the build process.
  if (!entity) {
    console.log(`Failed to fetch resource: ${path?.jsonapi?.individual ?? ''}`);
    return {
      notFound: true,
      revalidate: parseInt(REVALIDATE_TTL),
    };
  }

  // Unpublished translations and event pages without a published translation
  // for this locale redirect to the source language, not a localized fallback.
  const missingEventTranslation = entity?.type === 'node--event'
    && !hasPublishedTranslation(entity as {translated_urls?: TranslatedUrls}, requestedLocale);
  if (!context.preview && (entity?.status === false || missingEventTranslation)) {
    if (!NEXT_BUILD_MODE) {
      if (context?.params?.slug) {
        const purgePath = `${requestedLocale ?? defaultLocale}/${context?.params?.slug?.join('/')}`;
        await purgeSlug(purgePath);
      }
      const isSourceLanguage = !requestedLocale || requestedLocale === defaultLocale;
      const sourceDestination = getSourceLanguageDestination(entity as {translated_urls?: TranslatedUrls; path?: {alias?: string}}, defaultLocale);
      if (sourceDestination && !isSourceLanguage) {
        return {
          redirect: {
            destination: sourceDestination,
            statusCode: 301,
          },
        };
      }
      let destination = requestedLocale !== defaultLocale ? `/${requestedLocale}` : '/';
      if (entity?.type === 'node--event') {
        destination += '/events';
      }
      return {
        redirect: {
          destination,
          statusCode: 301,
        },
      };
    }
    return {
      notFound: true,
      revalidate: parseInt(REVALIDATE_TTL),
    };
  }

  // Redirect expired event detail pages to the events listing.
  // today is still valid; empty end date is valid.
  // Highlighted events remain reachable after they expire.
  if (
    entity?.type === 'node--event' &&
    !NEXT_BUILD_MODE &&
    entity?.field_event_end_date?.length &&
    !entity?.field_event_highlighted
  ) {
    const today = new Date().toISOString().split('T')[0];
    if (entity.field_event_end_date < today) {
      const localePrefix = context.locale !== context.defaultLocale ? `/${context.locale}` : '';
      return {
        redirect: {
          destination: `${localePrefix}/events`,
          statusCode: 301,
        },
      };
    }
  }

  // Set the translated urls.
  translatedUrls = entity?.translated_urls;

  const {contactFormTitle, contactFormDisplayTitle, contactFormDescription} = extractContactFormFields(entity);

  const {
    entity: processedEntity,
    additionalContent,
    displayContactUsForm,
    contactUsFormTitle,
    hideUtilityMenu,
    hideFooterMenu,
    logoScrollToTop,
    newsletterPad,
    contactPortalPadded,
    revalidateTTL,
  } = await processEntity(entity, type, context, customMenu);
  entity = processedEntity;

  // Add a dynamic OG image to product and term pages.
  if (['node--product', 'taxonomy_term--industry_segments', 'taxonomy_term--product_type', 'taxonomy_term--product_class'].includes(entity?.type)) {
    addDynamicOGImage(entity);
  }

  // Loop over metatag data and strip out and cms urls, replacing with www urls.
  if (entity?.metatag?.length && process.env.NEXT_PUBLIC_DRUPAL_BASE_URL && process.env.NEXT_PUBLIC_SITE_URL) {
    entity.metatag = replaceLeadingCmsUrlDeep(entity.metatag, process.env.NEXT_PUBLIC_DRUPAL_BASE_URL, process.env.NEXT_PUBLIC_SITE_URL);
  }

  // After processEntity so single-product term pages keep a self-canonical
  // that matches the sitemap URL, including the locale prefix.
  sanitizeCanonicalMetatags(entity, {
    locale: context.locale,
    defaultLocale: context.defaultLocale,
    slug: context.params?.slug,
  });

  // The Newsletter is discontinued, and the Drupal Webform for ContactUs
  // is pending CRM integration. Currently, we are only using an ActOn Form
  // we'll simply pass through a title stub if we're enabling the contact
  // us form.
  let contactUs: DrupalWebform | null = null;
  if (displayContactUsForm) {
    contactUs = await getLeadgenWebform(context, contactUsFormTitle);
  }

  if (!NEXT_BUILD_MODE) {
    const end = performance.now();
    console.log(`Slug ${slug} took ${((end - start) / 1000).toFixed(2)} seconds to build.`)
  }

  const customMenuCTA: CustomMenuLink = entity?.field_landing_page_cta ?? entity?.field_term_cta;
  const menus = await getMenus(context);
  if (customMenuCTA?.title && customMenuCTA?.url && menus?.main?.length) {
    for (const item of menus.main) {
      if (item?.field_menu_item_cta) {
        item.title = customMenuCTA.title;
        item.url = customMenuCTA.url;
      }
    }
  }

  return {
    props: {
      entity: entity,
      entityInfo: {
        id: entity.id,
        type: entity.type
      },
      additionalContent,
      alertBanner: await getAlertBanner(context),
      webforms: {
        contactUs,
      },
      contactFormTitle,
      contactFormDisplayTitle,
      contactFormDescription,
      structuredData: parseSchemaOverride(entity?.field_schema_override),
      redirectTo,
      menus,
      blurMap: await getBlurMap(),
      metatag: entity?.metatag ?? null,
      newsletterPad: !newsletterPad,
      contactPortalPadded,
      customMenu,
      hideUtilityMenu,
      hideFooterMenu,
      logoScrollToTop,
      dictionary: await getDictionary(context?.locale),
      translatedUrls,
    },
    // Never pin a menu-less render for the full TTL.
    revalidate: menuAwareRevalidate(menus, revalidateTTL ? revalidateTTL : parseInt(REVALIDATE_TTL)),
  };
}

/**
 * Drupal stores the schema override as a JSON string.
 */
function parseSchemaOverride(value?: string): object | '' {
  if (!value?.trim()) {
    return '';
  }
  try {
    return JSON.parse(value);
  } catch (error) {
    console.error('[schema-override] invalid JSON, skipped', error);
    return '';
  }
}
